import React, { useState, useRef, useCallback, useMemo } from 'react';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Page, Toolbar, Sort } from '@syncfusion/ej2-react-grids';
import { useTranslation } from 'react-i18next';
import Papa from 'papaparse';
import moment from 'moment';
import { useCsvImportConfig } from '../hooks/useCsvImportConfig';
import { evaluateDefaultValue, evaluateRowFilter } from '../utils/csvImportJexl';
import { MaterialControllerService } from '../api/services/MaterialControllerService';
import { SystemSettingControllerService } from '../api/services/SystemSettingControllerService';
import type { MaterialDto } from '../api/models/MaterialDto';
import { normalizeDemandQuantity } from '../utils/demandCsvImportPipeline';
import { parseLocalDateTime, toLocalDateString, toLocalDateTimeString } from '../utils/localDateTime';

export interface EntityField {
  field: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'enum' | 'boolean';
  required?: boolean;
  enumValues?: string[];
  defaultValue?: any;
}

export interface ValidationError {
  type: 'REQUIRED_FIELD_MISSING' | 'INVALID_DATA_TYPE' | 'INVALID_ENUM_VALUE' | 'PARSE_ERROR';
  field: string;
  rowNumber: number;
  value?: any;
  message: string;
  csvData?: any;
}

export interface ImportSummary {
  totalRows: number;
  successfulImports: number;
  failedImports: number;
  errors: ValidationError[];
  warnings: Array<{
    rowNumber: number;
    message: string;
    field?: string;
    value?: any;
  }>;
}

export interface CsvImportDialogProps<T> {
  isOpen: boolean;
  onClose: () => void;
  /** When called from the dialog during batch import, options.isBatchItem is true so the parent can suppress per-item toasts. */
  onImport: (data: T[], options?: { isBatchItem?: boolean }) => Promise<void>;
  entityFields: EntityField[];
  entityName: string;
  entityType?: string;
  importOptions?: {
    allowPartialImport?: boolean;
    batchSize?: number;
    validateBeforeImport?: boolean;
    showDetailedErrors?: boolean;
    autoRollbackOnFailure?: boolean;
    /** Materials for lookupMaterial converter (e.g. by article number). If not provided, loaded when needed for demands. */
    materialsForLookup?: MaterialDto[];
    /** When true: onImport is called once with all the data (no DB write inside the dialog). For the step 1 wizard: saving happens only on 'Next'. */
    deferredSave?: boolean;
    /** Run parameters that are preset and locked (cannot be changed). Key = field name, value = preset value. */
    fixedRunParameterValues?: Record<string, string>;
  };
}

export function CsvImportDialog<T>({
  isOpen,
  onClose,
  onImport,
  entityFields,
  entityName,
  entityType,
  importOptions = {}
}: CsvImportDialogProps<T>) {
  const { t, i18n } = useTranslation(['common']);
  const resolvedEntityType = entityType || entityName;
  const { config, isLoading: configLoading } = useCsvImportConfig(resolvedEntityType);
  const [csvData, setCsvData] = useState<any[]>([]);
  const [_csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [fieldMappings, setFieldMappings] = useState<Record<string, string>>({});
  const [previewData, setPreviewData] = useState<T[]>([]);
  const [_isProcessing, setIsProcessing] = useState(false);
  const [importSummary, setImportSummary] = useState<ImportSummary | null>(null);
  const [_showSummary, setShowSummary] = useState(false);
  const [currentStep, setCurrentStep] = useState<'upload' | 'runParameters' | 'preview' | 'importing' | 'summary'>('upload');
  const [runParameterValues, setRunParameterValues] = useState<Record<string, string>>({});
  const [encoding, setEncoding] = useState<string>('UTF-8');
  const [delimiter, setDelimiter] = useState<string>(',');
  const [previewRows, setPreviewRows] = useState<any[]>([]);
  const [previewHeaders, setPreviewHeaders] = useState<string[]>([]);
  const [parseErrorMessage, setParseErrorMessage] = useState<string | null>(null);
  const [missingColumnsWarning, setMissingColumnsWarning] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [materialsCache, setMaterialsCache] = useState<MaterialDto[] | null>(null);
  const [showImportOnlyValidDialog, setShowImportOnlyValidDialog] = useState(false);
  const [deliveryTimes, setDeliveryTimes] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const decimalSeparator = config?.decimalSeparator || ',';
  // eslint-disable-next-line react-hooks/exhaustive-deps -- stabilize to prevent re-renders; changes only when config changes
  const runParameterFields = useMemo(() => config?.runParameterFields ?? [], [config?.runParameterFields]);
  const runParameterFieldsSet = useMemo(() => new Set(runParameterFields), [runParameterFields]);

  React.useEffect(() => {
    if (!isOpen || !runParameterFields.includes('dueTime')) return;
    SystemSettingControllerService.getDeliveryTimes()
      .then((times) => setDeliveryTimes(Array.isArray(times) ? times : []))
      .catch(() => setDeliveryTimes([]));
  }, [isOpen, runParameterFields]);

  /** Get value from CSV row by column name; matches case-insensitively so "ARTNR" and "ArtNr" both work. */
  const getCsvRowValue = useCallback((row: any, columnName: string): any => {
    if (!row || !columnName) return undefined;
    if (Object.prototype.hasOwnProperty.call(row, columnName)) return row[columnName];
    const key = Object.keys(row).find((k) => k.trim().toLowerCase() === columnName.trim().toLowerCase());
    return key !== undefined ? row[key] : undefined;
  }, []);

  const {
    batchSize = 50,
    materialsForLookup,
    deferredSave = false,
    fixedRunParameterValues = {}
  } = importOptions;


  // Reset state when dialog opens
  React.useEffect(() => {
    if (isOpen) {
      setCurrentStep('upload');
      setCsvData([]);
      setCsvHeaders([]);
      setFieldMappings({});
      setPreviewData([]);
      setImportSummary(null);
      setShowSummary(false);
      setPreviewRows([]);
      setPreviewHeaders([]);
      setParseErrorMessage(null);
      setMissingColumnsWarning(null);
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (config?.fieldMapping) {
      setFieldMappings(config.fieldMapping);
    }
    if (config?.encoding) {
      setEncoding(config.encoding);
    }
    if (config?.separator) {
      setDelimiter(config.separator);
    }
  }, [config?.fieldMapping, config?.encoding, config?.separator]);

  const normalizedValueMappers = useMemo(() => {
    const valueMappers = config?.valueMappers as Record<string, any> | undefined;
    if (!valueMappers) return {};
    const normalized: Record<string, { function?: string; format?: string; strategy?: string; sourceColumn?: string }> = {};
    Object.entries(valueMappers).forEach(([field, mapper]) => {
      if (!mapper) return;
      if (typeof mapper === 'string') {
        normalized[field] = { function: mapper };
      } else if (typeof mapper === 'object') {
        normalized[field] = {
          function: mapper.function ?? mapper.name,
          format: mapper.format ?? mapper.pattern,
          strategy: mapper.strategy,
          sourceColumn: mapper.sourceColumn
        };
      }
    });
    return normalized;
  }, [config?.valueMappers]);

  // Lookup-Typ comes from valueMappers.materialLookup.strategy; fallback to 'articleNumber' if materialLookup exists but strategy missing (e.g. old config)
  const materialLookupStrategy = useMemo(() => {
    const mappers = config?.valueMappers as Record<string, any> | undefined;
    const materialLookup = mappers?.materialLookup;
    const strategy = materialLookup != null && typeof materialLookup === 'object'
      ? (materialLookup.strategy ?? 'articleNumber')
      : (materialLookup != null ? 'articleNumber' : null);
    return strategy;
  }, [config?.valueMappers]);

  const needsMaterialsForLookup = Boolean(materialLookupStrategy);

  React.useEffect(() => {
    if (!needsMaterialsForLookup || materialsForLookup !== undefined) return;
    let cancelled = false;
    MaterialControllerService.findAll()
      .then((list) => {
        if (!cancelled) {
          setMaterialsCache(list);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setMaterialsCache([]);
          console.warn('[CsvImport] Material-Lookup: failed to load materials', err);
        }
      });
    return () => { cancelled = true; };
  }, [needsMaterialsForLookup, materialsForLookup]);

  // Parse CSV for preview with proper encoding support
  const parseCsvPreview = (file: File) => {
    parseCsvPreviewWithSettings(file, encoding, delimiter);
  };

  // Parse CSV for preview with specific encoding and delimiter settings
  const parseCsvPreviewWithSettings = (file: File, targetEncoding: string, targetDelimiter: string) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let content = e.target?.result as string;
        
        // Convert content based on selected encoding
        if (targetEncoding !== 'UTF-8') {
          content = convertEncoding(content, targetEncoding);
        }
        
        // Skip pre-header rows if configured
        const skipRows = config?.skipRows ?? 0;
        if (skipRows > 0) {
          const lines = content.split(/\r\n|\n|\r/);
          content = lines.slice(skipRows).join('\n');
        }

        // Parse CSV content with Papa.parse (no preview limit – show all rows in step 1)
        Papa.parse(content, {
          header: true,
          skipEmptyLines: true,
          delimiter: targetDelimiter,
          complete: (results: Papa.ParseResult<any>) => {
            if (results.errors.length > 0) {
              const firstError = results.errors[0];
              const msg = firstError?.message ?? String(firstError);
              setParseErrorMessage(
                t('csvImport.parseErrorWithDetail', 'CSV konnte nicht gelesen werden: {{detail}}. Bitte Trennzeichen und Kodierung prüfen.', { detail: msg })
              );
              setPreviewRows([]);
              setPreviewHeaders([]);
              setMissingColumnsWarning(null);
              return;
            }

            const data = results.data as any[];
            const headers = data.length > 0 ? Object.keys(data[0] || {}) : [];
            const hasNoData = data.length === 0;
            const hasNoHeaders = headers.length === 0 || (headers.length === 1 && headers[0] === '');

            if (hasNoData || hasNoHeaders) {
              setParseErrorMessage(
                t('csvImport.parseNoColumns', 'Keine Daten oder Spalten erkannt. Bitte Trennzeichen (z.B. ; oder ,) und Kodierung in der Konfiguration prüfen.')
              );
              setPreviewRows([]);
              setPreviewHeaders([]);
              setMissingColumnsWarning(null);
              return;
            }

            setParseErrorMessage(null);
            setPreviewRows(data);
            setPreviewHeaders(headers);

            // Warn if configured CSV columns are missing in the file
            const configuredColumns = config?.fieldMapping ? Object.values(config.fieldMapping).filter((c): c is string => Boolean(c?.trim())) : [];
            const missing = configuredColumns.filter((col) => !headers.includes(col));
            if (missing.length > 0) {
              setMissingColumnsWarning(
                t('csvImport.missingColumns', 'Folgende konfigurierten Spalten fehlen in der CSV: {{list}}. Bitte Spaltennamen oder Konfiguration anpassen.', { list: missing.join(', ') })
              );
            } else {
              setMissingColumnsWarning(null);
            }
          }
        });
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        setParseErrorMessage(
          t('csvImport.parseErrorWithDetail', 'CSV konnte nicht gelesen werden: {{detail}}. Bitte Trennzeichen und Kodierung prüfen.', { detail: msg })
        );
        setPreviewRows([]);
        setPreviewHeaders([]);
        setMissingColumnsWarning(null);
      }
    };
    
    reader.onerror = () => {
      setParseErrorMessage(t('csvImport.parseErrorRead', 'Datei konnte nicht gelesen werden.'));
      setPreviewRows([]);
      setPreviewHeaders([]);
      setMissingColumnsWarning(null);
    };
    
    // Read file as text with the selected encoding
    reader.readAsText(file, targetEncoding);
  };

  // Convert text from one encoding to another
  const convertEncoding = (text: string, targetEncoding: string): string => {
    try {
      // For ANSI/Windows-1252, we need to handle byte conversion
      if (targetEncoding === 'Windows-1252' || targetEncoding === 'ISO-8859-1') {
        // Convert common German umlauts and special characters
        const umlautMap: Record<string, string> = {
          'ä': 'ä', 'ö': 'ö', 'ü': 'ü', 'Ä': 'Ä', 'Ö': 'Ö', 'Ü': 'Ü',
          'ß': 'ß', 'é': 'é', 'è': 'è', 'à': 'à', 'ù': 'ù',
          'á': 'á', 'í': 'í', 'ó': 'ó', 'ú': 'ú', 'ñ': 'ñ'
        };
        
        // Replace umlauts with their proper representations
        let converted = text;
        Object.entries(umlautMap).forEach(([original, replacement]) => {
          converted = converted.replace(new RegExp(original, 'g'), replacement);
        });
        
        return converted;
      }
      
      return text;
    } catch (error) {
      console.error('Encoding conversion error:', error);
      return text;
    }
  };

  // Handle file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    
    // Parse only when config is available (delimiter/skipRows)
    if (configLoading || !config) {
      return;
    }

    // Parse for preview first
    parseCsvPreview(file);

    // Don't automatically advance to mapping step - stay on upload step
    // The user will click "Continue" when ready
  };

  // Re-parse preview when config/encoding/delimiter arrive after selection
  React.useEffect(() => {
    if (!isOpen || !selectedFile) return;
    parseCsvPreviewWithSettings(selectedFile, encoding, delimiter);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- parseCsvPreviewWithSettings is a local function, adding it would trigger on every render
  }, [isOpen, selectedFile, encoding, delimiter, config?.skipRows]);

  // Pre-fill run parameter values from defaultValues when entering runParameters step
  React.useEffect(() => {
    if (currentStep !== 'runParameters' || runParameterFields.length === 0) return;
    const defaults = config?.defaultValues ?? {};
    setRunParameterValues((prev) => {
      let next = { ...prev };
      for (const fn of runParameterFields) {
        // Fixed values have the highest priority and always override.
        if (fixedRunParameterValues[fn] != null) {
          next[fn] = fixedRunParameterValues[fn];
          continue;
        }
        if (next[fn] != null && String(next[fn]).trim() !== '') continue;
        const def = defaults[fn];
        if (def) {
          try {
            next[fn] = String(evaluateDefaultValue(def, {}));
          } catch {
            /* keep empty */
          }
        }
      }
      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fixedRunParameterValues object identity changes every render; content is stable
  }, [currentStep, runParameterFields, config?.defaultValues]);

  // Parse CSV and go to preview (shared by Continue from upload when no run params, and from runParameters step)
  const doParseAndGoToPreview = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let content = e.target?.result as string;
        if (encoding !== 'UTF-8') {
          content = convertEncoding(content, encoding);
        }
        const skipRows = config?.skipRows ?? 0;
        if (skipRows > 0) {
          const lines = content.split(/\r\n|\n|\r/);
          content = lines.slice(skipRows).join('\n');
        }
        Papa.parse(content, {
          header: true,
          skipEmptyLines: true,
          delimiter: delimiter,
          complete: (results: Papa.ParseResult<any>) => {
            if (results.errors.length > 0) {
              console.error('CSV parsing errors:', results.errors);
              return;
            }
            const data = results.data as any[];
            const headers = Object.keys(data[0] || {});
            let dataRows = data;
            if (config?.rowFilter?.trim()) {
              dataRows = dataRows.filter((row: any) => evaluateRowFilter(config.rowFilter!, row));
            }
            setCsvData(dataRows);
            setCsvHeaders(headers);
            const resolvedMappings: Record<string, string> = (config?.fieldMapping && Object.keys(config.fieldMapping).length > 0)
              ? config.fieldMapping
              : (() => {
                const autoMappings: Record<string, string> = {};
                headers.forEach(header => {
                  const matchingField = entityFields.find(field =>
                    field.field.toLowerCase() === header.toLowerCase() ||
                    field.label.toLowerCase() === header.toLowerCase()
                  );
                  if (matchingField) {
                    autoMappings[matchingField.field] = header;
                  }
                });
                return autoMappings;
              })();
            setFieldMappings(resolvedMappings);
            setPreviewData(transformDataWithErrors(dataRows));
            setCurrentStep('preview');
          }
        });
      } catch (error) {
        console.error('Error parsing CSV:', error);
      }
    };
    reader.readAsText(file, encoding);
  };

  // Continue from upload: either go to runParameters step or parse and go to preview
  const handleContinueToMapping = () => {
    if (csvData.length > 0) {
      setCurrentStep('preview');
      return;
    }
    const file = fileInputRef.current?.files?.[0] ?? selectedFile;
    if (!file) return;
    if (runParameterFields.length > 0) {
      setCurrentStep('runParameters');
      return;
    }
    doParseAndGoToPreview(file);
  };

  // Continue from runParameters step: parse CSV and go to preview (runParameterValues already in state)
  const handleContinueFromRunParameters = () => {
    const file = fileInputRef.current?.files?.[0] ?? selectedFile;
    if (!file) return;
    doParseAndGoToPreview(file);
  };


  // Materials to use for Material-Lookup (prop or loaded cache)
  const materialsForConverter = materialsForLookup ?? materialsCache ?? [];


  /** Lookup material by key (article number or name). Used only for field materialLookup; strategy comes from Lookup column. */
  const doMaterialLookup = (key: string, strategy: string): { materialId: string; materialName: string } => {
    const k = String(key ?? '').trim();
    const strat = strategy || 'articleNumber';
    if (!k) throw new Error(t('csvImport.errors.lookupValueEmpty'));
    if (strat === 'articleNumber') {
      const material = materialsForConverter.find(
        (m) => m.articleNumber != null && String(m.articleNumber).trim() === k
      );
      if (!material?.uuid) {
        const sample = materialsForConverter.slice(0, 5).map((m: any) => m.articleNumber);
        console.warn('[CsvImport] Material-Lookup: no material found for articleNumber', JSON.stringify(k), 'Sample articleNumbers in list:', sample);
        throw new Error(t('csvImport.errors.materialNotFoundArticleNumber', { value: k }));
      }
      return { materialId: material.uuid, materialName: material.name ?? '' };
    }
    if (strat === 'name') {
      const material = materialsForConverter.find(
        (m) => m.name != null && String(m.name).trim() === k
      );
      if (!material?.uuid) {
        console.warn('[CsvImport] Material-Lookup: no material found for name', JSON.stringify(k));
        throw new Error(t('csvImport.errors.materialNotFoundName', { value: k }));
      }
      return { materialId: material.uuid, materialName: material.name ?? '' };
    }
    throw new Error(t('csvImport.errors.unknownLookupStrategy', { strategy: strat }));
  };

  // Validate a single row
  const applyConverter = (
    value: any,
    converter?: { function?: string; format?: string; strategy?: string; sourceColumn?: string },
    _row?: any
  ) => {
    if (!converter?.function) {
      return value;
    }

    const format = converter.format;
    switch (converter.function) {
      case 'parseDate': {
        if (!format) throw new Error(t('csvImport.errors.missingFormatParseDate'));
        const parsed = moment(value, format, true);
        if (!parsed.isValid()) throw new Error(t('csvImport.errors.invalidDate'));
        return parsed.format('YYYY-MM-DD');
      }
      case 'parseDateTime': {
        if (!format) throw new Error(t('csvImport.errors.missingFormatParseDateTime'));
        const parsed = moment(value, format, true);
        if (!parsed.isValid()) throw new Error(t('csvImport.errors.invalidDateTime'));
        // Local wall clock for LocalDateTime fields such as deliveries.expectedDate
        return parsed.format('YYYY-MM-DDTHH:mm:ss');
      }
      case 'extractDate': {
        const parsed = format ? moment(value, format, true) : moment(value);
        if (!parsed.isValid()) throw new Error(t('csvImport.errors.invalidDateTime'));
        return parsed.format('YYYY-MM-DD');
      }
      case 'extractTime': {
        const parsed = format ? moment(value, format, true) : moment(value);
        if (!parsed.isValid()) throw new Error(t('csvImport.errors.invalidDateTime'));
        return parsed.format('HH:mm');
      }
      case 'parseInteger': {
        const normalized = String(value || '')
          .replace(/\s/g, '')
          .replace(decimalSeparator === ',' ? /\./g : /,/g, '')
          .replace(/[^\d-]/g, '');
        const parsed = parseInt(normalized, 10);
        if (Number.isNaN(parsed)) throw new Error(t('csvImport.errors.invalidInteger'));
        return parsed;
      }
      case 'mapDemandSource':
      case 'mapDemandType': {
        const normalized = String(value || '').trim().toUpperCase();
        if (!normalized) return 'ORDER';
        return normalized === 'FORECAST' ? 'FORECAST' : 'ORDER';
      }
      default:
        return value;
    }
  };


  // Transform CSV data to entity format; per-row errors are caught and attached as _rowError so preview still shows
  const transformDataWithErrors = (data: any[]): (T & { _rowError?: string; _rowIndex?: number })[] => {
    const defaultValues = config?.defaultValues ?? {};
    return data.map((row, rowIndex) => {
      const transformed: any = { _rowIndex: rowIndex + 1 };

      try {
        entityFields.forEach(field => {
          const csvValue = getCsvRowValue(row, fieldMappings[field.field]);
          const converter = normalizedValueMappers[field.field];
          const hasCsvValue = csvValue !== undefined && csvValue !== null && csvValue !== '';

          if (hasCsvValue) {
            if (field.field === 'materialLookup' && materialLookupStrategy) {
              let key: any = csvValue;
              if (converter?.function) {
                key = applyConverter(csvValue, converter, row);
              }
              const { materialId: mid, materialName: mname } = doMaterialLookup(String(key), materialLookupStrategy);
              transformed.materialId = mid;
              transformed.materialName = mname;
            } else {
              let transformedValue: any = csvValue;
              if (converter?.function) {
                transformedValue = applyConverter(csvValue, converter, row);
              } else {
                switch (field.type) {
                  case 'number':
                    transformedValue = Number(csvValue);
                    break;
                  case 'date':
                    transformedValue = toLocalDateTimeString(parseLocalDateTime(String(csvValue)));
                    break;
                  case 'boolean':
                    transformedValue = ['true', '1', 'yes'].includes(String(csvValue).toLowerCase());
                    break;
                  default:
                    break;
                }
              }
              transformed[field.field] = transformedValue;
            }
          } else if (runParameterFieldsSet.has(field.field) && runParameterValues[field.field] != null && String(runParameterValues[field.field]).trim() !== '') {
            const raw = String(runParameterValues[field.field]).trim();
            switch (field.type) {
              case 'date':
                transformed[field.field] = raw.includes('T') ? toLocalDateString(parseLocalDateTime(raw)) : raw;
                break;
              case 'number':
                transformed[field.field] = Number(raw);
                break;
              case 'boolean':
                transformed[field.field] = ['true', '1', 'yes'].includes(raw.toLowerCase());
                break;
              default:
                transformed[field.field] = raw;
            }
          } else if (defaultValues[field.field] != null && String(defaultValues[field.field]).trim() !== '') {
            transformed[field.field] = evaluateDefaultValue(String(defaultValues[field.field]), row);
          } else if (field.defaultValue !== undefined) {
            transformed[field.field] = field.defaultValue;
          }
        });

        if (resolvedEntityType === 'demands' || entityName === 'demand') {
          (transformed as Record<string, unknown>).quantity = normalizeDemandQuantity(transformed.quantity);
        }
      } catch (err: any) {
        transformed._rowError = err?.message ?? String(err);
        // For error rows still show available CSV data (quantity, date, etc.) so the grid is meaningful
        entityFields.forEach(field => {
          if (transformed[field.field] !== undefined) return;
          const csvVal = getCsvRowValue(row, fieldMappings[field.field]);
          if (csvVal !== undefined && csvVal !== null && String(csvVal).trim() !== '') {
            transformed[field.field] = csvVal;
          }
        });
      }

      return transformed as T & { _rowError?: string; _rowIndex?: number };
    });
  };

  /** Same as transformDataWithErrors but returns only valid rows (no _rowError), for import. */
  const transformData = (data: any[]): T[] => {
    return transformDataWithErrors(data).filter((r: any) => !r._rowError).map((r: any) => {
      const { _rowError, _rowIndex, ...rest } = r;
      return rest as T;
    });
  };


  // Run import with a list of items (used for full import or "only valid" import). skippedDueToPreviewErrors are rows not imported because of preview validation errors.
  const runImportWithItems = async (items: T[], totalRowsLabel: number, skippedDueToPreviewErrors = 0) => {
    const summary: ImportSummary = {
      totalRows: totalRowsLabel,
      successfulImports: 0,
      failedImports: skippedDueToPreviewErrors,
      errors: [],
      warnings: []
    };

    if (deferredSave) {
      try {
        await onImport(items);
        summary.successfulImports = items.length;
      } catch (error: any) {
        summary.errors.push({
          type: 'PARSE_ERROR',
          field: 'unknown',
          rowNumber: 0,
          message: error.message || t('csvImport.errors.unknownImportError', 'Unbekannter Fehler beim Import'),
          csvData: undefined
        });
        summary.failedImports = items.length;
      }
    } else {
      for (let i = 0; i < items.length; i += batchSize) {
        const batch = items.slice(i, i + batchSize);
        for (let j = 0; j < batch.length; j++) {
          const rowIndex = i + j;
          const item = batch[j];
          try {
            await onImport([item], { isBatchItem: true });
            summary.successfulImports++;
          } catch (error: any) {
            summary.errors.push({
              type: 'PARSE_ERROR',
              field: 'unknown',
              rowNumber: rowIndex + 1,
              message: error.message || t('csvImport.errors.unknownImportError', 'Unbekannter Fehler beim Import'),
              csvData: undefined
            });
            summary.failedImports++;
          }
        }
      }
    }

    setImportSummary(summary);
    setCurrentStep('summary');
  };

  // Start import process; if there are row errors in preview, show dialog first
  const handleImport = async () => {
    if (previewErrorCount > 0) {
      setShowImportOnlyValidDialog(true);
      return;
    }

    setIsProcessing(true);
    setCurrentStep('importing');
    try {
      const transformedData = transformData(csvData);
      await runImportWithItems(transformedData, csvData.length);
    } catch (error) {
      console.error('Import error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // User confirmed: import only rows without errors (from preview)
  const handleConfirmImportOnlyValid = async () => {
    setShowImportOnlyValidDialog(false);
    const validItems = (previewData as any[])
      .filter((r: any) => !r._rowError)
      .map((r: any) => {
        const { _rowError, _rowIndex, ...rest } = r;
        return rest as T;
      });

    if (validItems.length === 0) {
      return;
    }

    setIsProcessing(true);
    setCurrentStep('importing');
    try {
      await runImportWithItems(validItems, previewData.length, previewErrorCount);
    } catch (error) {
      console.error('Import error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Export errors to CSV
  const exportErrorsToCsv = () => {
    if (!importSummary) return;
    
    const errorCsv = importSummary.errors.map(error => ({
      'Row Number': error.rowNumber,
      'Field': error.field,
      'Value': error.value || '',
      'Error Message': error.message,
      'Original CSV Data': JSON.stringify(error.csvData)
    }));
    
    const csv = Papa.unparse(errorCsv);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `import_errors_${entityName}_${new Date().toISOString()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Close dialog and reset
  const handleClose = () => {
    setCurrentStep('upload');
    setRunParameterValues({});
    setRunParameterErrors({});
    setCsvData([]);
    setCsvHeaders([]);
    setFieldMappings({});
    setPreviewData([]);
    setImportSummary(null);
    setShowSummary(false);
    onClose();
  };

  const renderUploadStep = () => (
    <div className="flex flex-col h-full">
      <div className="flex-1 space-y-6">
        {!configLoading && !config && (
          <div className="p-4 border border-red-300 bg-red-50 rounded text-red-700">
            {t('csvImport.errors.missingConfig', { entityType: resolvedEntityType })}
          </div>
        )}
        {/* No loading banner here; keep the dialog clean on open */}
        {/* File Upload Section */}
        <div className="text-center">
          <div className="mb-6">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.txt"
              onChange={handleFileUpload}
              className="hidden"
            />
            <ButtonComponent
              onClick={() => fileInputRef.current?.click()}
              cssClass="e-primary"
              disabled={configLoading || !config}
            >
              {t('actions.selectCsvFile')}
            </ButtonComponent>
          </div>
          
          <p className="text-sm text-gray-600 max-w-md mx-auto">
            {t('messages.csvFileInstructions')}
          </p>
        </div>

        {/* Parse error / no columns message */}
        {parseErrorMessage && (
          <div className="mt-4 p-4 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm" role="alert">
            {parseErrorMessage}
          </div>
        )}

        {/* Missing configured columns warning */}
        {missingColumnsWarning && !parseErrorMessage && (
          <div className="mt-4 p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm" role="alert">
            {missingColumnsWarning}
          </div>
        )}

        {/* Live Preview Grid */}
        {previewRows.length > 0 && (
          <div>
            <h5 className="font-semibold mb-3 text-lg">{t('csvImport.rawDataView', 'Ansicht Rohdaten')}</h5>
            <div className="border rounded-lg">
              <GridComponent
                dataSource={previewRows}
                height="420px"
                allowPaging={false}
                allowSorting={true}
                allowFiltering={false}
                locale="de-DE"
              >
                <ColumnsDirective>
                  {previewHeaders.map((header) => (
                    <ColumnDirective
                      key={header}
                      field={header}
                      headerText={header}
                      width={280}
                      textAlign="Left"
                      valueAccessor={(_field, data) => String((data as any)[header] ?? '')}
                    />
                  ))}
                </ColumnsDirective>
                <Inject services={[Page, Toolbar, Sort]} />
              </GridComponent>
            </div>
          </div>
        )}
      </div>
      
      <div className="flex justify-between pt-4 border-t">
        <ButtonComponent onClick={handleClose} cssClass="e-outline">
          {t('actions.cancel')}
        </ButtonComponent>
        <ButtonComponent
          onClick={handleContinueToMapping}
          cssClass="e-primary"
          disabled={(previewRows.length === 0 && !csvData.length) || configLoading || !config}
        >
          {t('actions.continue')}
        </ButtonComponent>
      </div>
    </div>
  );

  const [runParameterErrors, setRunParameterErrors] = useState<Record<string, string>>({});

  const renderRunParametersStep = () => {
    const fieldsToShow = runParameterFields
      .map((fieldName) => entityFields.find((f) => f.field === fieldName))
      .filter((f): f is EntityField => f != null)
      .sort((a, b) => entityFields.findIndex((f) => f.field === a.field) - entityFields.findIndex((f) => f.field === b.field));
    const defaultValues = config?.defaultValues ?? {};
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 space-y-6">
          <p className="text-sm text-gray-600">
            {t('csvImport.runParameterStepDescription', 'Geben Sie die Werte ein, die für alle Zeilen dieses Imports gelten sollen (wenn in der CSV kein Wert steht).')}
          </p>
          <div className="space-y-4">
            {fieldsToShow.map((ef) => {
              const value = runParameterValues[ef.field] ?? (defaultValues[ef.field] ? String(evaluateDefaultValue(defaultValues[ef.field], {})) : '');
              const fieldLabel = t(`demand.grid.field.${ef.field}`, ef.label);
              const isDueTimeField = ef.field === 'dueTime';
              const isFixed = ef.field in fixedRunParameterValues;
              const error = runParameterErrors[ef.field];
              return (
                <div key={ef.field} className="flex flex-col gap-1">
                  <label className="font-medium text-sm" htmlFor={`run-param-${ef.field}`}>
                    {fieldLabel}{ef.required ? ' *' : ''}
                    {isFixed && (
                      <span className="ml-2 text-xs font-normal text-gray-500">
                        {t('csvImport.fixedRunParameter', '(festgelegt)')}
                      </span>
                    )}
                  </label>
                  {isDueTimeField && deliveryTimes.length > 0 ? (
                    <div className="max-w-xs">
                      <DropDownListComponent
                        id={`run-param-${ef.field}`}
                        dataSource={deliveryTimes.map((timeStr) => ({ value: timeStr.slice(0, 5), text: timeStr.slice(0, 5) }))}
                        fields={{ text: 'text', value: 'value' }}
                        value={value?.slice(0, 5) ?? (deliveryTimes[0]?.slice(0, 5) ?? '')}
                        change={(e: any) => {
                          if (isFixed) return;
                          setRunParameterValues((prev) => ({ ...prev, [ef.field]: e.value ?? '' }));
                          if (runParameterErrors[ef.field]) setRunParameterErrors((prev) => ({ ...prev, [ef.field]: '' }));
                        }}
                        placeholder=""
                        floatLabelType="Never"
                        cssClass={error ? 'e-error' : ''}
                        enabled={!isFixed}
                      />
                    </div>
                  ) : ef.type === 'date' ? (
                    <input
                      id={`run-param-${ef.field}`}
                      type="date"
                      className={`e-input border rounded px-3 py-2 w-full max-w-xs ${isFixed ? 'bg-gray-100 cursor-not-allowed text-gray-600' : ''} ${error ? 'border-red-500' : ''}`}
                      value={value.slice(0, 10)}
                      readOnly={isFixed}
                      disabled={isFixed}
                      onChange={(e) => {
                        if (isFixed) return;
                        setRunParameterValues((prev) => ({ ...prev, [ef.field]: e.target.value }));
                        if (runParameterErrors[ef.field]) setRunParameterErrors((prev) => ({ ...prev, [ef.field]: '' }));
                      }}
                    />
                  ) : ef.type === 'number' ? (
                    <input
                      id={`run-param-${ef.field}`}
                      type="number"
                      className={`e-input border rounded px-3 py-2 w-full max-w-xs ${error ? 'border-red-500' : ''}`}
                      value={value}
                      onChange={(e) => {
                        setRunParameterValues((prev) => ({ ...prev, [ef.field]: e.target.value }));
                        if (runParameterErrors[ef.field]) setRunParameterErrors((prev) => ({ ...prev, [ef.field]: '' }));
                      }}
                    />
                  ) : (
                    <input
                      id={`run-param-${ef.field}`}
                      type="text"
                      className={`e-input border rounded px-3 py-2 w-full max-w-xs ${error ? 'border-red-500' : ''}`}
                      value={value}
                      onChange={(e) => {
                        setRunParameterValues((prev) => ({ ...prev, [ef.field]: e.target.value }));
                        if (runParameterErrors[ef.field]) setRunParameterErrors((prev) => ({ ...prev, [ef.field]: '' }));
                      }}
                    />
                  )}
                  {error && <span className="text-xs text-red-600">{error}</span>}
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex justify-between pt-4 border-t">
          <ButtonComponent onClick={() => setCurrentStep('upload')} cssClass="e-outline">
            {t('actions.back')}
          </ButtonComponent>
          <ButtonComponent
            onClick={() => {
              const required = fieldsToShow.filter((ef) => ef.required);
              const errors: Record<string, string> = {};
              required.forEach((ef) => {
                const value = runParameterValues[ef.field] ?? (config?.defaultValues?.[ef.field] ? String(evaluateDefaultValue(config.defaultValues[ef.field], {})) : '');
                if (value == null || String(value).trim() === '') {
                  errors[ef.field] = t('validation.required');
                }
              });
              setRunParameterErrors(errors);
              if (Object.keys(errors).length === 0) handleContinueFromRunParameters();
            }}
            cssClass="e-primary"
          >
            {t('actions.continue')}
          </ButtonComponent>
        </div>
      </div>
    );
  };

  const isDemandPreview = resolvedEntityType === 'demands' || entityName === 'demand';
  const demandPreviewColumnWidth = 280;
  const demandPreviewColumns = [
    {
      field: 'materialName',
      headerText: t('demand.grid.field.materialName'),
      width: demandPreviewColumnWidth,
      valueAccessor: (_field: string, data: any) => String(data?.materialName ?? '')
    },
    {
      field: 'demandDate',
      headerText: t('demand.grid.field.demandDate'),
      width: 160,
      valueAccessor: (_field: string, data: any) => {
        if (!data?.demandDate) return '';
        const date = new Date(data.demandDate);
        return date.toLocaleDateString(i18n.language === 'de' ? 'de-DE' : 'en-US', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
      }
    },
    {
      field: 'dueTime',
      headerText: t('demand.grid.field.dueTime'),
      width: 120,
      valueAccessor: (_field: string, data: any) => {
        if (!data?.dueTime) return '-';
        return String(data.dueTime).substring(0, 5);
      }
    },
    {
      field: 'quantity',
      headerText: t('demand.grid.field.quantity'),
      width: 120,
      valueAccessor: (_field: string, data: any) => String(data?.quantity ?? '')
    },
    // demandId computed by backend; status, source, priority and notes are internal only - not in the preview
    {
      field: 'customerOrderNumber',
      headerText: t('demand.grid.field.customerOrderNumber'),
      width: demandPreviewColumnWidth,
      valueAccessor: (_field: string, data: any) => String(data?.customerOrderNumber ?? '')
    }
  ];

  const previewRowsWithErrors = (previewData as any[]).filter((r: any) => r._rowError);
  const previewValidCount = (previewData as any[]).filter((r: any) => !r._rowError).length;
  const previewErrorCount = previewRowsWithErrors.length;

  // Aggregate preview errors by type for compact display (e.g. "Unbekannte Artikelnummern: 133, 157, ...")
  const aggregatedPreviewErrors = useMemo(() => {
    const articleNumbers: string[] = [];
    const articleRowCount: number[] = [];
    const materialNames: string[] = [];
    const other: { message: string; row: number }[] = [];
    const reArticle = /Material mit Artikelnummer (.+?) nicht gefunden|Material with article number (.+?) not found/i;
    const reName = /Material mit Namen (.+?) nicht gefunden|Material with name (.+?) not found/i;
    previewRowsWithErrors.forEach((r: any) => {
      const msg = r._rowError ?? '';
      const mArticle = reArticle.exec(msg);
      const mName = reName.exec(msg);
      if (mArticle) {
        const val = (mArticle[1] ?? mArticle[2] ?? '').trim();
        if (val) {
          const idx = articleNumbers.indexOf(val);
          if (idx === -1) {
            articleNumbers.push(val);
            articleRowCount.push(1);
          } else {
            articleRowCount[idx]++;
          }
        }
      } else if (mName) {
        const val = (mName[1] ?? mName[2] ?? '').trim();
        if (val && !materialNames.includes(val)) materialNames.push(val);
      } else if (msg) {
        other.push({ message: msg, row: r._rowIndex ?? 0 });
      }
    });
    const lines: string[] = [];
    if (articleNumbers.length > 0) {
      const maxShow = 15;
      const values = articleNumbers.length <= maxShow
        ? articleNumbers.join(', ')
        : articleNumbers.slice(0, maxShow).join(', ') + t('csvImport.aggregatedErrorsMore', ' … (+ {{count}} weitere)', { count: articleNumbers.length - maxShow });
      const totalArticleRows = articleRowCount.reduce((a, b) => a + b, 0);
      lines.push(t('csvImport.aggregatedErrorsUnknownArticleNumbersWithRows', 'Unbekannte Artikelnummern ({{uniqueCount}} verschiedene, in {{rowCount}} Zeilen): {{values}}', {
        values,
        uniqueCount: articleNumbers.length,
        rowCount: totalArticleRows
      }));
    }
    if (materialNames.length > 0) {
      const maxShow = 15;
      const values = materialNames.length <= maxShow
        ? materialNames.join(', ')
        : materialNames.slice(0, maxShow).join(', ') + t('csvImport.aggregatedErrorsMore', ' … (+ {{count}} weitere)', { count: materialNames.length - maxShow });
      lines.push(t('csvImport.aggregatedErrorsUnknownMaterialNames', { values }));
    }
    if (other.length > 0) {
      const firstMsg = other[0].message;
      const rows = other.map((o) => o.row).join(', ');
      lines.push(t('csvImport.aggregatedErrorsOther') + ': ' + firstMsg + (other.length > 1 ? ' (Zeilen ' + rows + ')' : ''));
    }
    return lines;
  }, [previewRowsWithErrors, t]);

  const renderPreviewStep = () => (
    <div className="flex flex-col h-full">
      <div className="flex-1 space-y-6">
        <div>
          <h5 className="font-semibold mb-3 text-lg">{t('csvImport.demandPreview', 'Vorschau Bedarfe')}</h5>
          {previewErrorCount > 0 && (
            <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <p className="mb-2">{t('csvImport.previewRowErrors', { errorCount: previewErrorCount, total: previewData.length })}</p>
              {aggregatedPreviewErrors.map((line, i) => (
                <div key={i} className="mt-1 text-amber-700">
                  {line}
                </div>
              ))}
            </div>
          )}
          <div className="border rounded-lg">
            <GridComponent
              dataSource={previewData}
              height="470px"
              locale="de-DE"
              allowPaging={false}
              allowSorting={true}
              allowFiltering={false}
              rowDataBound={(args: any) => {
                const rowError = args?.data?._rowError;
                if (rowError) {
                  args.row.style.backgroundColor = '#fff1f2';
                  if (args.row.getAttribute('title') !== rowError) {
                    args.row.setAttribute('title', rowError);
                  }
                }
              }}
            >
              <ColumnsDirective>
                {isDemandPreview
                  ? demandPreviewColumns.map((col) => (
                    <ColumnDirective
                      key={col.field}
                      field={col.field}
                      headerText={col.headerText}
                      width={(col as any).width ?? 260}
                      textAlign="Left"
                      valueAccessor={col.valueAccessor}
                    />
                  ))
                  : entityFields.map((field) => (
                    <ColumnDirective
                      key={field.field}
                      field={field.field}
                      headerText={field.label}
                      width={280}
                      textAlign="Left"
                      valueAccessor={(_field, data) => String((data as any)[field.field] ?? '')}
                    />
                  ))}
              </ColumnsDirective>
              <Inject services={[Page, Toolbar, Sort]} />
            </GridComponent>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {t('csvImport.recordCount', 'Anzahl Datensätze: {{count}}', { count: csvData.length })}
          </p>
        </div>
      </div>
      
      <div className="flex justify-between pt-4 border-t">
        <ButtonComponent
          onClick={() => setCurrentStep(runParameterFields.length > 0 ? 'runParameters' : 'upload')}
          cssClass="e-outline"
        >
          {t('actions.back')}
        </ButtonComponent>
        <ButtonComponent
          onClick={handleImport}
          cssClass="e-primary"
        >
          {t('actions.import')}
        </ButtonComponent>
      </div>
    </div>
  );

  const renderImportingStep = () => (
    <div className="flex flex-col h-full">
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center space-y-6">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <div>
            <p className="text-lg font-medium">{t('messages.importingData')}</p>
            <p className="text-sm text-gray-600 mt-2">
              {t('messages.pleaseWait')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSummaryStep = () => (
    <div className="flex flex-col h-full">
      <div className="flex-1 space-y-6">
        <div className="text-center">
          <h5 className="font-semibold text-xl mb-4">
            {importSummary?.failedImports === 0 
              ? t('messages.importCompleted') 
              : t('messages.importCompletedWithErrors')}
          </h5>
          
          <div className="grid grid-cols-3 gap-6 mb-6">
            <div className="text-center p-4 bg-green-100 rounded-lg">
              <div className="text-3xl font-bold text-green-600">{importSummary?.successfulImports}</div>
              <div className="text-sm text-green-800">{t('labels.successful')}</div>
            </div>
            <div className="text-center p-4 bg-red-100 rounded-lg">
              <div className="text-2xl font-bold text-red-600">{importSummary?.failedImports}</div>
              <div className="text-sm text-red-800">{t('labels.failed')}</div>
            </div>
            <div className="text-center p-4 bg-blue-100 rounded-lg">
              <div className="text-3xl font-bold text-blue-600">{importSummary?.totalRows}</div>
              <div className="text-sm text-blue-800">{t('labels.total')}</div>
            </div>
          </div>
        </div>

        {/* Summary below the KPIs: what exactly failed (or succeeded) */}
        {importSummary && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <h6 className="font-semibold text-lg text-gray-800 mb-2">
              {t('labels.failedImportOverviewTitle')}
            </h6>
            {importSummary.errors.length > 0 ? (
              <>
                <p className="text-sm text-gray-600 mb-3">
                  {t('labels.failedImportOverviewIntro')}
                </p>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm text-gray-500">
                    {importSummary.errors.length} {t('labels.failed')}
                  </span>
                  <ButtonComponent
                    onClick={exportErrorsToCsv}
                    cssClass="e-outline"
                  >
                    {t('actions.exportErrors')}
                  </ButtonComponent>
                </div>
                <div className="max-h-60 overflow-y-auto border rounded-lg">
                  <table className="min-w-full">
                    <thead>
                      <tr className="bg-gray-100 border-b">
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">{t('labels.row')}</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">{t('labels.field')}</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">{t('labels.value')}</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">{t('labels.errorMessage')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importSummary.errors.map((error, index) => (
                        <tr key={index} className="border-b border-gray-200 hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">{error.rowNumber}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{error.field}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{error.value ?? ''}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{error.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-600">{t('labels.noFailedImportsMessage')}</p>
            )}
          </div>
        )}
      </div>
      
      <div className="flex justify-end pt-4 border-t">
        <ButtonComponent onClick={handleClose} cssClass="e-primary">
          {t('actions.close')}
        </ButtonComponent>
      </div>
    </div>
  );

  const getStepContent = () => {
    switch (currentStep) {
      case 'upload': return renderUploadStep();
      case 'runParameters': return renderRunParametersStep();
      case 'preview': return renderPreviewStep();
      case 'importing': return renderImportingStep();
      case 'summary': return renderSummaryStep();
      default: return renderUploadStep();
    }
  };

  const getDialogTitle = () => {
    // Always show "CSV importieren" for all steps
    return t('labels.importCsv', 'CSV importieren');
  };

  return (
    <DialogComponent
      width="1200px"
      height="800px"
      isModal={true}
      visible={isOpen}
      header={getDialogTitle()}
      showCloseIcon={true}
      closeOnEscape={true}
      allowDragging={true}
      target="#root"
      animationSettings={{ effect: 'None' }}
      beforeClose={(e) => {
        e.cancel = true;
        handleClose();
      }}
    >
      <div className="h-full p-6">
        {getStepContent()}
      </div>

      {showImportOnlyValidDialog && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40" role="dialog" aria-modal="true" aria-labelledby="import-only-valid-title">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md mx-4">
            <h3 id="import-only-valid-title" className="text-lg font-semibold text-gray-900 mb-3">
              {t('csvImport.importOnlyValidTitle', 'Fehlerhafte Zeilen')}
            </h3>
            <p className="text-gray-700 mb-4">
              {t('csvImport.importOnlyValidMessage', 'In {{errorCount}} Zeilen sind Fehler aufgetreten (z. B. Material nicht gefunden). Nur die {{validCount}} fehlerfreien Datensätze importieren?', { errorCount: previewErrorCount, validCount: previewValidCount })}
            </p>
            <div className="flex justify-end gap-3">
              <ButtonComponent
                type="button"
                cssClass="e-outline"
                onClick={() => setShowImportOnlyValidDialog(false)}
              >
                {t('actions.cancel')}
              </ButtonComponent>
              <ButtonComponent
                type="button"
                cssClass="e-primary"
                onClick={handleConfirmImportOnlyValid}
              >
                {t('csvImport.importOnlyValidConfirm', 'Nur fehlerfreie importieren')}
              </ButtonComponent>
            </div>
          </div>
        </div>
      )}
    </DialogComponent>
  );
}
